import React from 'react';

import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import type { DropResult, OnDragEndResponder } from 'react-beautiful-dnd';

import EditorPanelIcon from '@atlaskit/icon/glyph/editor/panel';

import {
  Table,
  createColumnWidths,
  createColumnsWidths,
} from '@qality/common-components/src/atoms/Table';
import { ContentWithIconTooltip } from '@qality/common-components/src/molecules/ContentWithIconTooltip';
import { LoadingButtonWithTooltip } from '@qality/common-components/src/molecules/LoadingButtonWithTooltip';

import {
  MAX_NUMBER_OF_STATUSES,
  useTestCaseExecutionStatusPageContext,
} from '../../TestCaseExecutionStatusPageProvider';
import { HeaderCellWrapperStyled } from './SchemeTable.styled';
import TestCaseExecutionEditStatusRow from './TestCaseExecutionEditStatusRow';
import TestCaseExecutionStatusRow from './TestCaseExecutionStatusRow';

const STATUS_TYPE_TOOLTIP =
  'You can assign your status a special type, depending on whether executions have or do not have bugs. Statuses or executions will be listed according to their status type.';

const IS_DEFAULT_TOOLTIP =
  'Default status is the initial status of the Test Case Execution.';

type Columns =
  | 'dnd'
  | 'status'
  | 'color'
  | 'statusType'
  | 'isDefault'
  | 'actions';

const StaticColumnsWidths = createColumnsWidths<Columns>({
  dnd: createColumnWidths('0%'),
  status: createColumnWidths('38%'),
  color: createColumnWidths('28%'),
  statusType: createColumnWidths('21%'),
  isDefault: createColumnWidths('13%'),
  actions: createColumnWidths('0%'),
});

const EditColumnsWidths = createColumnsWidths<Columns>({
  dnd: createColumnWidths('2%'),
  status: createColumnWidths('33%'),
  color: createColumnWidths('25%'),
  statusType: createColumnWidths('21%'),
  isDefault: createColumnWidths('13%'),
  actions: createColumnWidths('6%'),
});

export default function SchemeTable(): JSX.Element {
  const {
    statuses,
    isEditScreen,
    createStatus,
    loading,
    isMaxNumberOfStatuses,
    updateStatusPosition,
  } = useTestCaseExecutionStatusPageContext();

  const ColumnsWidths = isEditScreen ? EditColumnsWidths : StaticColumnsWidths;
  const NUMBER_OF_COLUMNS = Object.keys(ColumnsWidths).length;

  const onDragEnd = async (result: DropResult): OnDragEndResponder => {
    await updateStatusPosition(result);
  };

  return (
    <Table>
      <Table.Head>
        <Table.Tr>
          {isEditScreen && (
            <Table.DndTh
              widths={ColumnsWidths.dnd}
              aria-label='Drag and drop'
            />
          )}
          <Table.Th widths={ColumnsWidths.status}>
            <HeaderCellWrapperStyled>Status</HeaderCellWrapperStyled>
          </Table.Th>
          <Table.Th widths={ColumnsWidths.color}>Color</Table.Th>
          <Table.Th widths={ColumnsWidths.statusType}>
            <ContentWithIconTooltip
              tooltipContent={STATUS_TYPE_TOOLTIP}
              iconAfter={
                <EditorPanelIcon label='editor-panel-icon' size='small' />
              }
            >
              Status type
            </ContentWithIconTooltip>
          </Table.Th>
          <Table.Th widths={ColumnsWidths.isDefault}>
            <ContentWithIconTooltip
              tooltipContent={IS_DEFAULT_TOOLTIP}
              iconAfter={
                <EditorPanelIcon label='editor-panel-icon' size='small' />
              }
            >
              Is default
            </ContentWithIconTooltip>
          </Table.Th>
          {isEditScreen && (
            <Table.Th widths={ColumnsWidths.actions}>Actions</Table.Th>
          )}
        </Table.Tr>
      </Table.Head>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable
          key='droppable-statuses-list'
          droppableId='droppable-statuses-list'
        >
          {(provided) => {
            return (
              <Table.Body
                innerRef={provided.innerRef}
                {...provided.droppableProps}
              >
                {statuses.map((status, index) => {
                  return isEditScreen ? (
                    <TestCaseExecutionEditStatusRow
                      key={status.id}
                      status={status}
                      index={index}
                    />
                  ) : (
                    <TestCaseExecutionStatusRow
                      key={status.id}
                      status={status}
                    />
                  );
                })}
                {provided.placeholder}
              </Table.Body>
            );
          }}
        </Droppable>
      </DragDropContext>
      {isEditScreen && (
        <Table.Footer>
          <Table.Tr>
            <Table.Td colSpan={NUMBER_OF_COLUMNS}>
              <Table.ContentHelper mt={24}>
                <LoadingButtonWithTooltip
                  loadingButtonProps={{
                    appearance: 'primary',
                    isLoading: loading === 'create-status',
                    isDisabled: isMaxNumberOfStatuses,
                    onClick: createStatus,
                  }}
                  tooltipProps={{
                    content:
                      isMaxNumberOfStatuses &&
                      `The maximum number of statuses is ${MAX_NUMBER_OF_STATUSES}.`,
                    position: 'bottom-start',
                  }}
                >
                  Add new status
                </LoadingButtonWithTooltip>
              </Table.ContentHelper>
            </Table.Td>
          </Table.Tr>
        </Table.Footer>
      )}
    </Table>
  );
}
